import React, { useState } from 'react';
import QuickBookModal from './QuickBookModal';
import s from './QuickBookModal.module.css';

interface QuickBookLauncherProps {
    branchId: number | null;
    branchName?: string;
    label?: string;
    onBooked?: () => void;
    /**
     * AB#1752 — Staff-first mode. When provided, the launcher passes them
     * through to `QuickBookModal` which opens with the staff pre-selected
     * (mockup §D). Use the ghost variant for in-card placements.
     */
    lockedStaffId?: number | null;
    lockedStaffName?: string | null;
    lockedStaffGender?: 'Male' | 'Female' | null;
    /** Visual variant — `primary` for header CTAs, `ghost` for card-row actions. */
    variant?: 'primary' | 'ghost';
}

/**
 * AB#1751 / AB#1752 — Reusable launcher button + modal mount.
 *
 * Drop into any page header. Disabled gracefully when `branchId` isn't
 * resolved yet (e.g. branches still loading on Appointments page).
 *
 * Staff-first variant (mockup §D): pass `lockedStaffId` + `lockedStaffName`
 * and the modal opens pinned to that staff. Used on ManageStaffSection
 * staff cards so the owner can book a slot without leaving the staff
 * management surface.
 */
const QuickBookLauncher: React.FC<QuickBookLauncherProps> = ({
    branchId, branchName, label, onBooked,
    lockedStaffId, lockedStaffName, lockedStaffGender,
    variant = 'primary',
}) => {
    const [open, setOpen] = useState(false);
    const disabled = branchId == null || branchId <= 0;
    const defaultLabel = lockedStaffId != null && lockedStaffName
        ? `Quick book with ${lockedStaffName} +`
        : 'Quick book +';
    const buttonClass = variant === 'ghost' ? `${s.launcher} ${s.launcherGhost}` : s.launcher;

    return (
        <>
            <button
                type="button"
                className={buttonClass}
                onClick={() => setOpen(true)}
                disabled={disabled}
                title={disabled ? 'Pick a branch to book against first.' : undefined}
            >
                {label ?? defaultLabel}
            </button>
            {open && branchId != null && (
                <QuickBookModal
                    branchId={branchId}
                    branchName={branchName}
                    lockedStaffId={lockedStaffId ?? undefined}
                    lockedStaffName={lockedStaffName ?? undefined}
                    lockedStaffGender={lockedStaffGender ?? null}
                    onClose={() => setOpen(false)}
                    onBooked={onBooked}
                />
            )}
        </>
    );
};

export default QuickBookLauncher;
